import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Building2, TrendingUp, MapPin, BarChart3, FileText, Users, Check, ArrowRight, Star, Calculator, RefreshCw, Home, Hammer } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png";

const LandingPage = () => {
  const navigate = useNavigate();

  const handleCheckRents = () => {
    navigate("/login?redirect=/dashboard");
  };

  const features = [
    {
      icon: TrendingUp,
      title: "AI-Powered Estimates",
      description: "Get accurate rent estimates powered by GPT-5.2 analysis and real market data."
    },
    {
      icon: MapPin,
      title: "Neighborhood Analysis",
      description: "Interactive maps showing comparable properties and market trends in your area."
    },
    {
      icon: Calculator,
      title: "Deal Calculators",
      description: "BRRRR, Fix & Flip, Buy & Hold, and Subject-To calculators with integrated rental analysis."
    },
    {
      icon: FileText,
      title: "Export & Share",
      description: "Generate professional PDF reports and shareable links for clients and investors."
    }
  ];

  const tiers = [
    {
      name: "Basic",
      price: "$10",
      annualPrice: "$120/yr",
      monthlyPrice: "$18",
      period: "/mo (annual)",
      features: ["10 reports/mo or 100/year", "AI-powered insights", "BRRRR, Flip, Buy-Hold & Subject-To calculators", "CSV & PDF exports", "Share reports", "$0.15/report overage"],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Pro",
      price: "$18",
      annualPrice: "$216/yr",
      monthlyPrice: "$26",
      period: "/mo (annual)",
      features: ["100 reports/mo or 1000/year", "All Basic features", "All 4 deal calculators with rental analysis", "Property comparison tool", "Custom branding", "Priority support"],
      cta: "Go Pro",
      popular: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-12 w-auto" />
              
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
              <Link to="/login" className="text-slate-600 hover:text-slate-900 transition-colors">Sign In</Link>
              <Button onClick={() => navigate("/register")} className="bg-navy-900 hover:bg-navy-800" data-testid="header-get-started-btn">
                Get Started
              </Button>
            </nav>
            <Button variant="ghost" className="md:hidden" onClick={() => navigate("/login")}>
              Sign In
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(248, 250, 252, 0.9), rgba(248, 250, 252, 0.95)), url('https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-sm font-medium mb-6">
                <Star className="w-4 h-4 mr-2" />
                Trusted by 10,000+ Investors
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy-900 mb-6 leading-tight">
                Know Your Property's
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-emerald-500"> True Rental Value</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0">
                AI-powered rental analysis for landlords and investors. Get instant rent estimates, market comparisons, and investment calculators.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-6 max-w-xl mx-auto lg:mx-0">
                <Button 
                  onClick={handleCheckRents}
                  size="lg" 
                  className="h-14 px-8 bg-teal-500 hover:bg-teal-400 text-navy-900 font-bold shadow-lg shadow-teal-500/20"
                  data-testid="hero-check-rents-btn"
                >
                  Check Rents
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={() => navigate("/calculators")}
                  size="lg" 
                  variant="outline"
                  className="h-14 px-8 border-slate-300 text-slate-700 font-bold hover:bg-slate-100"
                  data-testid="hero-calculators-btn"
                >
                  <Calculator className="mr-2 h-5 w-5" />
                  Try Calculators Free
                </Button>
              </div>

              <p className="text-sm text-slate-500">
                Start with 5 free reports • Plans from $10/month • Cancel anytime
              </p>
            </div>
            
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-teal-400 to-emerald-500 rounded-2xl blur-2xl opacity-20"></div>
                <Card className="relative shadow-2xl border-0">
                  <CardHeader className="pb-2">
                    <CardDescription>Rent Estimate</CardDescription>
                    <CardTitle className="text-3xl font-data text-navy-900">$2,850<span className="text-lg text-slate-500">/mo</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Market Average</span>
                        <span className="font-data text-navy-900">$2,720</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Market Range</span>
                        <span className="font-data text-navy-900">$2,200 - $3,400</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Comparable Properties</span>
                        <span className="font-data text-navy-900">15</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full mt-4">
                        <div className="h-2 bg-teal-500 rounded-full w-3/4"></div>
                      </div>
                      <p className="text-xs text-emerald-600 font-medium">↑ 4.8% above market average</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
              Everything You Need to Analyze Rentals
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Professional-grade tools for landlords, property managers, and real estate investors.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="p-6 border-slate-200 hover:shadow-lg transition-shadow group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <feature.icon className="h-6 w-6 text-teal-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-navy-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Deal Calculators Section */}
      <section className="py-20 lg:py-32 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Powerful Deal Calculators
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Analyze any investment strategy with our built-in calculators. Run rental analysis directly from the calculator to get accurate market rents.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-white/10 border-white/20 p-6 hover:bg-white/15 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center mb-4">
                <RefreshCw className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-2">BRRRR Calculator</h3>
              <p className="text-slate-300 text-sm mb-3">
                Buy, Rehab, Rent, Refinance, Repeat. Calculate cash-out potential and long-term cash flow.
              </p>
              <ul className="space-y-1 text-xs text-slate-400">
                <li className="flex items-center"><Check className="h-3 w-3 text-teal-400 mr-2" />Refinance analysis</li>
                <li className="flex items-center"><Check className="h-3 w-3 text-teal-400 mr-2" />Cash-on-cash return</li>
              </ul>
            </Card>
            
            <Card className="bg-white/10 border-white/20 p-6 hover:bg-white/15 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center mb-4">
                <Hammer className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-2">Fix & Flip Calculator</h3>
              <p className="text-slate-300 text-sm mb-3">
                Analyze flip deals with holding costs, rehab budgets, ROI, and profit margins.
              </p>
              <ul className="space-y-1 text-xs text-slate-400">
                <li className="flex items-center"><Check className="h-3 w-3 text-orange-400 mr-2" />Net profit calculation</li>
                <li className="flex items-center"><Check className="h-3 w-3 text-orange-400 mr-2" />Annualized ROI</li>
              </ul>
            </Card>
            
            <Card className="bg-white/10 border-white/20 p-6 hover:bg-white/15 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center mb-4">
                <Home className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-2">Buy & Hold Calculator</h3>
              <p className="text-slate-300 text-sm mb-3">
                Long-term rental analysis with cash flow, cap rate, DSCR, and break-even calculations.
              </p>
              <ul className="space-y-1 text-xs text-slate-400">
                <li className="flex items-center"><Check className="h-3 w-3 text-blue-400 mr-2" />Monthly cash flow</li>
                <li className="flex items-center"><Check className="h-3 w-3 text-blue-400 mr-2" />Cap rate & DSCR</li>
              </ul>
            </Card>
            
            <Card className="bg-white/10 border-white/20 p-6 hover:bg-white/15 transition-colors border-purple-400/50">
              <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-lg text-white mb-2">Subject-To Calculator</h3>
              <p className="text-slate-300 text-sm mb-3">
                Creative financing with subject-to and seller finance analysis. Take over existing mortgages.
              </p>
              <ul className="space-y-1 text-xs text-slate-400">
                <li className="flex items-center"><Check className="h-3 w-3 text-purple-400 mr-2" />Subject-to mortgage</li>
                <li className="flex items-center"><Check className="h-3 w-3 text-purple-400 mr-2" />Seller finance terms</li>
              </ul>
            </Card>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-slate-400 mb-4">All calculators include PDF and Excel export with rental market data</p>
            <Button 
              onClick={() => navigate("/calculators")} 
              className="bg-teal-500 hover:bg-teal-400 text-navy-900 font-bold"
              size="lg"
            >
              Try Calculators Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Feature Image Section */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-6">
                Make Confident Investment Decisions
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Our AI analyzes thousands of comparable properties to give you accurate rent estimates and market insights. Know if you're pricing too high, too low, or just right.
              </p>
              <ul className="space-y-4">
                {["Instant rent estimates for any address", "Compare to market averages and trends", "AI-generated investment recommendations", "Export professional reports"].map((item, i) => (
                  <li key={i} className="flex items-center text-slate-700">
                    <Check className="h-5 w-5 text-teal-500 mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/register")} 
                className="mt-8 bg-navy-900 hover:bg-navy-800"
                data-testid="cta-get-started-btn"
              >
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg" 
                alt="Modern apartment interior" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Start with 5 free reports. Plans from $10/month billed annually.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {tiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`relative p-8 ${tier.popular ? 'border-2 border-purple-500 shadow-xl' : 'border-2 border-teal-500'}`}
                data-testid={`pricing-card-${tier.name.toLowerCase()}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                    Best Value
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-heading font-bold text-xl text-navy-900 mb-2">{tier.name}</h3>
                  <div className="flex items-baseline justify-center">
                    <span className="font-data text-4xl font-bold text-navy-900">{tier.price}</span>
                    <span className="text-slate-500 ml-1">{tier.period}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">or {tier.monthlyPrice}/mo monthly</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-sm text-slate-600">
                      <Check className={`h-4 w-4 ${tier.popular ? 'text-purple-500' : 'text-teal-500'} mr-3 flex-shrink-0`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => navigate("/register")}
                  className={`w-full ${tier.popular ? 'bg-purple-500 hover:bg-purple-400 text-white' : 'bg-teal-500 hover:bg-teal-400 text-navy-900'}`}
                  data-testid={`pricing-btn-${tier.name.toLowerCase()}`}
                >
                  {tier.cta}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-navy-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Maximize Your Rental Income?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join thousands of landlords and investors using Rentlytix to make smarter decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate("/register")}
              size="lg"
              className="bg-teal-500 hover:bg-teal-400 text-navy-900 font-bold shadow-lg"
              data-testid="cta-start-free-btn"
            >
              Start Free Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => navigate("/login")}
              size="lg"
              variant="outline"
              className="border-slate-500 text-white hover:bg-slate-800"
            >
              Sign In
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <img src="https://customer-assets.emergentagent.com/job_property-analyzer-23/artifacts/1qqdoef6_Untitled_design-removebg-preview.png" alt="Rentlytix" className="h-14 w-auto" />
              
            </div>
            <p className="text-slate-400 text-sm">
              © 2025 Rentlytix. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
